import { Stack, Typography } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { Controller } from 'react-hook-form'
import { DEFAULT_ERROR_MESSAGE, SECURED_EMAIL_WARNING_HELPER_TEXT, useFormContextRequester } from '../utils/_utils'
import { ButtonSelectorOption } from '../../Common/buttonSelector/_props'
import UserFormField from '../../User/UserFormField'

const SecuredEmail = () => {
    const { control, watch, setValue } = useFormContextRequester()

    const validRpps = !!watch('rppsNum')
    const securedEmailList = watch('securedEmailList')

    const options = useMemo<ButtonSelectorOption<string>[]>(() => {
        if (!securedEmailList) return []

        return securedEmailList.map<ButtonSelectorOption<string>>((securedEmail) => ({
            label: securedEmail,
            value: securedEmail,
        }))
    }, [securedEmailList])

    const hasOptions = options.length > 0

    useEffect(() => {
        if (securedEmailList?.length === 1) {
            setValue('securedEmail', securedEmailList[0])
        }
    }, [securedEmailList, setValue])

    const noSecureEmailText = 'Aucun courriel sécurisé'

    const sideTooltip = useMemo(
        () => (
            <Stack gap="1rem">
                <Typography>
                    Le courriel sécurisé (importé de l'annuaire santé) est obligatoire pour que le requérant puisse
                    valider les demandes de téléexpertise : vérifiez au préalable que le courriel renseigné soit à jour.
                </Typography>
            </Stack>
        ),
        [],
    )

    return (
        <Controller
            control={control}
            name="securedEmail"
            rules={{
                required: hasOptions ? DEFAULT_ERROR_MESSAGE : undefined,
            }}
            render={({ field, fieldState: { error } }) => {
                const isLocked = options.length < 2
                const displayNoValueText = validRpps && !hasOptions
                const fieldLength = field.value?.trim().length ?? 0
                const isValid = fieldLength > 0 || (fieldLength === 0 && !hasOptions && validRpps)
                const isWarning = validRpps && !hasOptions

                return (
                    <UserFormField
                        id="securedEmailAnchor"
                        variant="select"
                        label="Courriel sécurisé"
                        type="default"
                        name={field.name}
                        locked={isLocked}
                        status={{
                            error,
                            valid: isValid && !isWarning,
                            warning: isWarning,
                        }}
                        helperText={isWarning ? SECURED_EMAIL_WARNING_HELPER_TEXT : undefined}
                        readonly={isLocked}
                        placeholder="Courriel sécurisé"
                        noValueText={displayNoValueText ? noSecureEmailText : undefined}
                        value={field.value}
                        disabled={isLocked}
                        sideTooltip={sideTooltip}
                        onChange={(e) => {
                            field.onChange(e)
                        }}
                        options={options}
                        dataCyScope="UserField"
                    />
                )
            }}
        />
    )
}

export default SecuredEmail
